import { use } from "react";
import { BikeContext } from "../context/bikeContext";
import { usePreferences } from "../hooks/useAccount";
import {
  CountingFrom,
  PostSettingsBody,
  WheelsSettings as WS,
} from "../http/onlyclicksApi";
import { translate } from "../i18n";
import { Language } from "../types";
import { UseFormReturn } from "./Form";
import Panel from "./Panel";
import Setting from "./Setting";
import styles from "./WheelsSettings.module.css";

interface Props {
  wheels?: Omit<WS, "id" | "created_at">;
  isEditMode: boolean;
  form: UseFormReturn<PostSettingsBody>;
  countingFrom: CountingFrom;
}

const WheelsSettings = ({ wheels, isEditMode, form, countingFrom }: Props) => {
  const { data: preferences } = usePreferences();
  const { bike } = use(BikeContext);
  const i18n = translate(Language.English);
  const mode = isEditMode ? "edit" : "view";

  if (!preferences || !bike) return null;

  return (
    <Panel title={i18n.wheels}>
      {bike.show_wheels ? (
        <ol className={styles.foo}>
          {preferences.tire_pressure_unit === "bar" && (
            <>
              <li>
                <form.Field name="wheels.front_bar">
                  {(field) => (
                    <Setting
                      label={i18n.front_pressure}
                      value={{
                        curr: field.state.value,
                        prev: wheels?.front_bar?.curr,
                      }}
                      unit={i18n.tirePressureUnit(
                        preferences.tire_pressure_unit,
                      )}
                      minValue={0}
                      maxValue={10}
                      step={0.1}
                      mode={mode}
                      onChange={(frontBar) => field.handleChange(frontBar)}
                      countingFrom={countingFrom}
                    />
                  )}
                </form.Field>
              </li>
              <li>
                <form.Field name="wheels.rear_bar">
                  {(field) => (
                    <Setting
                      label={i18n.rear_pressure}
                      value={{
                        curr: field.state.value,
                        prev: wheels?.rear_bar?.curr,
                      }}
                      unit={i18n.tirePressureUnit(
                        preferences.tire_pressure_unit,
                      )}
                      minValue={0}
                      maxValue={10}
                      step={0.1}
                      mode={mode}
                      onChange={(rearBar) => field.handleChange(rearBar)}
                      countingFrom={countingFrom}
                    />
                  )}
                </form.Field>
              </li>
            </>
          )}
          {preferences.tire_pressure_unit === "psi" && (
            <>
              <li>
                <form.Field name="wheels.front_psi">
                  {(field) => (
                    <Setting
                      label={i18n.front_pressure}
                      value={{
                        curr: field.state.value,
                        prev: wheels?.front_psi?.curr,
                      }}
                      unit={i18n.tirePressureUnit(
                        preferences.tire_pressure_unit,
                      )}
                      minValue={0}
                      step={0.5}
                      mode={mode}
                      onChange={(frontPsi) => field.handleChange(frontPsi)}
                      countingFrom={countingFrom}
                    />
                  )}
                </form.Field>
              </li>
              <li>
                <form.Field name="wheels.rear_psi">
                  {(field) => (
                    <Setting
                      label={i18n.rear_pressure}
                      value={{
                        curr: field.state.value,
                        prev: wheels?.rear_psi?.curr,
                      }}
                      unit={i18n.tirePressureUnit(
                        preferences.tire_pressure_unit,
                      )}
                      minValue={0}
                      step={0.5}
                      mode={mode}
                      onChange={(rearPsi) => field.handleChange(rearPsi)}
                      countingFrom={countingFrom}
                    />
                  )}
                </form.Field>
              </li>
            </>
          )}
        </ol>
      ) : (
        <span>Wheels not configured.</span>
      )}
    </Panel>
  );
};

export default WheelsSettings;
